import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import client from "../api/client";
import { TTL, cacheDelete, cacheGet, cacheSet, userCacheKey } from "../lib/cache";
import { useAuth } from "./AuthContext";

const CACHE_RESOURCE = "conversations";

const ConversationsContext = createContext(null);

export function ConversationsProvider({ children }) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (revalidate = false) => {
      if (!isAuthenticated || !user) {
        setConversations([]);
        setLoading(false);
        return;
      }

      const key = userCacheKey(user, CACHE_RESOURCE);
      if (!revalidate) {
        const cached = cacheGet(key);
        if (cached !== null) {
          setConversations(cached);
          setLoading(false);
        }
      } else {
        setLoading(true);
      }

      try {
        const [chatRes, devRes] = await Promise.all([
          client.get("/conversations"),
          client.get("/developer/conversations"),
        ]);
        const chatItems = chatRes.data.map((c) => ({ ...c, type: "chat" }));
        const devItems = devRes.data.map((c) => ({ ...c, type: "developer" }));
        const merged = [...chatItems, ...devItems].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
        cacheSet(key, merged, TTL.CONVERSATIONS);
        setConversations(merged);
      } catch {
        // A license lockout (402, or 403 with detail.code === "license_required")
        // is handled globally by the axios interceptor in src/api/client.js.
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, user],
  );

  useEffect(() => {
    load();
  }, [load]);

  const selectConversation = useCallback(
    (id) => {
      const conv = conversations.find((c) => c.id === id);
      if (!conv) return;
      if (conv.type === "developer") navigate(`/developer/${id}`);
      else navigate(`/chat/${id}`);
    },
    [conversations, navigate],
  );

  const createConversation = useCallback(async () => {
    const response = await client.post("/conversations", { title: "New conversation" });
    cacheDelete(userCacheKey(user, CACHE_RESOURCE));
    navigate(`/chat/${response.data.id}`);
  }, [user, navigate]);

  const createDeveloperConversation = useCallback(() => {
    navigate("/developer");
  }, [navigate]);

  const renameConversation = useCallback(
    async (id, title) => {
      const conv = conversations.find((c) => c.id === id);
      const endpoint =
        conv?.type === "developer"
          ? `/developer/conversations/${id}`
          : `/conversations/${id}`;
      const response = await client.patch(endpoint, { title });
      const updated = conversations.map((c) =>
        c.id === id ? { ...c, title: response.data.title } : c,
      );
      const key = userCacheKey(user, CACHE_RESOURCE);
      cacheSet(key, updated, TTL.CONVERSATIONS);
      setConversations(updated);
    },
    [conversations, user],
  );

  const deleteConversation = useCallback(
    async (id) => {
      const conv = conversations.find((c) => c.id === id);
      const endpoint =
        conv?.type === "developer"
          ? `/developer/conversations/${id}`
          : `/conversations/${id}`;
      await client.delete(endpoint);
      const updated = conversations.filter((c) => c.id !== id);
      const key = userCacheKey(user, CACHE_RESOURCE);
      cacheSet(key, updated, TTL.CONVERSATIONS);
      setConversations(updated);
    },
    [conversations, user],
  );

  const value = useMemo(
    () => ({
      conversations,
      loading,
      selectConversation,
      createConversation,
      createDeveloperConversation,
      renameConversation,
      deleteConversation,
      reload: () => load(true),
    }),
    [
      conversations,
      loading,
      selectConversation,
      createConversation,
      createDeveloperConversation,
      renameConversation,
      deleteConversation,
      load,
    ],
  );

  return (
    <ConversationsContext.Provider value={value}>{children}</ConversationsContext.Provider>
  );
}

export function useConversationsContext() {
  const context = useContext(ConversationsContext);
  if (!context) {
    throw new Error("useConversationsContext must be used within ConversationsProvider");
  }
  return context;
}
